import type {
  AnyFormApi,
  FormValidateFn,
  ValidationError,
  ValidationErrorMap
} from '@tanstack/react-form';

/**
 * Workaround to preserve validation errors on field blur.
 *
 * https://github.com/TanStack/form/pull/726
 * TanStack Form clears `onMount` errors when a field is touched
 * (e.g., blurred), even if the value hasn't changed. This prematurely sets
 * `canSubmit` to true.
 *
 * https://github.com/TanStack/form/discussions/1784
 * Attaching this function to `onBlur` will force an `onChange` validation,
 * effectively preserving the errors set by `onMount`.
 *
 * Warning: Do not return errors directly from an `onBlur` validator. Otherwise,
 * errors won't clear while the user is typing (`onChange`), which degrades UX.
 *
 */
export function triggerOnChangeValidator<TFormData>({
  formApi
}: Parameters<FormValidateFn<TFormData>>[0]) {
  void formApi.validate('change');
}

export function getNestedFieldErrors(
  errors: {
    fields: Record<string, { errors: ValidationError[]; errorMap: ValidationErrorMap }>;
  },
  parentFieldName: string
) {
  return Object.fromEntries(
    Object.entries(errors.fields).filter(
      ([fieldName]) =>
        fieldName === parentFieldName ||
        fieldName.startsWith(`${parentFieldName}.`) ||
        fieldName.startsWith(`${parentFieldName}[`)
    )
  );
}

export function removeNestedFieldErrors(
  formApi: Pick<AnyFormApi, 'getAllErrors' | 'setFieldMeta'>,
  fieldName: string
) {
  const errors = getNestedFieldErrors(formApi.getAllErrors(), fieldName);
  for (const key in errors) {
    formApi.setFieldMeta(key, prev => ({ ...prev, errorMap: {} }));
  }
}
